import type { AxiosResponse } from 'axios';

import type { InformativeMessage, StreamChunk } from '../shared/types';
import { StreamChunkContentType } from '../shared/types';

// !!! make sure splitter is same as backend
const STREAM_CHUNK_SPLITTER = '\n';

export const tryParseJson = <T>(json: string) => {
    try {
        return JSON.parse(json) as T;
    } catch {
        return undefined;
    }
};

const parseReceivedText = <TChunk>(receivedText: string, parsedLengthToSkip: number) => {
    const chunkJsonArr = receivedText
        .split(STREAM_CHUNK_SPLITTER)
        .filter((s) => s.trim())
        .slice(parsedLengthToSkip);

    const newlyParsedChunks: TChunk[] = [];
    chunkJsonArr.forEach((json) => {
        // !!! try-catch block is important, the receivedText read from http response body
        // may break at any position of whole stream buffer
        const chunk = tryParseJson<TChunk>(json);
        if (chunk) {
            newlyParsedChunks.push(chunk);
        }
    });

    return newlyParsedChunks;
};

export const parseStreamResponseByAxios = async <TChunk>(
    response: AxiosResponse,
    onParse: (newlyParsedChunks: TChunk[], chunks: TChunk[]) => Promise<void>
) => {
    const readableStream = response.data as ReadableStream;
    if (!readableStream || typeof readableStream.pipeTo !== 'function') {
        throw new Error(
            'please set `adapter` as "fetch", `responseType` as "stream" in AxiosRequestConfig'
        );
    }

    const reader = readableStream.getReader();
    const decoder = new TextDecoder('utf-8');
    let receivedText = '';
    const chunks: TChunk[] = [];

    // eslint-disable-next-line no-constant-condition
    while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) {
            break;
        }

        const partialText = decoder.decode(value);
        receivedText += partialText;
        const newlyParsedChunks = parseReceivedText<TChunk>(receivedText, chunks.length);
        chunks.push(...newlyParsedChunks);

        // invoke callback only for valid parsing
        if (newlyParsedChunks.length) {
            // eslint-disable-next-line no-await-in-loop
            await onParse(newlyParsedChunks, chunks);
        }
    }

    return chunks;
};

export function getLatestTextChunkContent(chunks: StreamChunk[]) {
    const copiedArray = [...chunks];
    return (
        copiedArray
            .reverse()
            .find(({ contents }) => contents[0].contentType === StreamChunkContentType.TEXT)
            ?.contents[0].content || ''
    );
}

/**
 * used when one chunk has structured informativeMessageMap
 */
export function getLatestInformativeMessageMap(
    chunks: StreamChunk[]
): Record<string, InformativeMessage> {
    const copiedArray = [...chunks];
    const chunk = copiedArray
        .reverse()
        .find(
            ({ contents }) => contents[0].contentType === StreamChunkContentType.INFORMATIVE_MESSAGE
        );
    if (!chunk) {
        return {};
    }
    return chunk.contents[0].content as unknown as Record<string, InformativeMessage>;
}

export function filterChunksByType(chunks: StreamChunk[], type: StreamChunkContentType) {
    return chunks.filter((chunk) => chunk.contents[0].contentType === type);
}

/**
 * used when one chunk has only one informativeMessage
 */
export function buildInformativeMessageMap(chunks: StreamChunk[]) {
    const filteredChunks = filterChunksByType(chunks, StreamChunkContentType.INFORMATIVE_MESSAGE);
    return filteredChunks.reduce((acc, chunk) => {
        const informativeMessage = tryParseJson<InformativeMessage>(chunk.contents[0].content);
        if (informativeMessage) {
            acc[informativeMessage.id] = informativeMessage;
        }
        return acc;
    }, {} as Record<string, InformativeMessage>);
}
